import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Bot, User, Loader2, AlertCircle, ChefHat, Share2 } from 'lucide-react';
import { getSharedContent } from '../../../shared/services/shareService';
import type { SharedContent } from '../types/share';
import { RestaurantCard } from '../../../shared/components/RestaurantCard';
import { ShareMetaTags } from '../components/ShareMetaTags';
import { logger } from '../../../shared/utils/logger';

export function SharePage() {
  const { shareToken } = useParams<{ shareToken: string }>();
  const navigate = useNavigate();
  const [content, setContent] = useState<SharedContent | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!shareToken) {
      setError('Missing share token');
      setIsLoading(false);
      return;
    }

    const fetchContent = async () => {
      try {
        setIsLoading(true);
        setError(null);
        const data = await getSharedContent(shareToken);
        setContent(data);
      } catch (err: any) {
        logger.error('Error fetching shared content:', err);
        setError(err.message || 'Failed to load shared content');
      } finally {
        setIsLoading(false);
      }
    };

    fetchContent();
  }, [shareToken]);

  if (isLoading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-orange-50 via-white to-red-50 flex items-center justify-center">
        <div className="text-center">
          <Loader2 className="w-12 h-12 text-orange-500 animate-spin mx-auto mb-4" />
          <p className="text-gray-600">加载中...</p>
        </div>
      </div>
    );
  }

  if (error || !content || !content.success) {
    const errorMessage = error || content?.error || '分享链接不存在或已失效';
    const isNetworkError = errorMessage.includes('无法连接到服务器') || errorMessage.includes('Failed to fetch');
    
    return (
      <div className="min-h-screen bg-gradient-to-br from-orange-50 via-white to-red-50 flex items-center justify-center px-4">
        <div className="text-center max-w-md">
          <AlertCircle className="w-16 h-16 text-red-500 mx-auto mb-4" />
          <h2 className="text-2xl font-bold text-gray-900 mb-2">无法加载分享内容</h2>
          <p className="text-gray-600 mb-2">
            {errorMessage}
          </p>
          {shareToken && (
            <p className="text-sm text-gray-500 mb-6">
              分享 Token: {shareToken}
            </p>
          )}
          {isNetworkError && (
            <p className="text-sm text-gray-500 mb-6">
              请检查网络连接或稍后重试
            </p>
          )}
          <div className="flex gap-3 justify-center">
            <button
              onClick={() => window.location.reload()}
              className="px-6 py-3 bg-gray-500 text-white rounded-lg hover:bg-gray-600 transition-colors"
            >
              刷新页面
            </button>
            <button
              onClick={() => navigate('/')}
              className="px-6 py-3 bg-orange-500 text-white rounded-lg hover:bg-orange-600 transition-colors"
            >
              返回首页
            </button>
          </div>
        </div>
      </div>
    );
  }

  // Generate share URL for meta tags
  const shareUrl = window.location.href;
  const shareTitle = content.query_text 
    ? `Fun with AI: ${content.query_text.substring(0, 50)}${content.query_text.length > 50 ? '...' : ''}`
    : 'Fun with AI - 餐厅推荐';
  const shareDescription = content.message_content 
    ? content.message_content.substring(0, 150) + (content.message_content.length > 150 ? '...' : '')
    : '发现您附近的优质餐厅推荐';

  return (
    <>
      {/* Simple Meta Tags - Text and URL only (no rich preview cards) */}
      <ShareMetaTags
        title={shareTitle}
        description={shareDescription}
        url={shareUrl}
      />

      <div className="min-h-screen bg-gradient-to-br from-orange-50 via-white to-red-50 py-8 px-4">
        <div className="max-w-4xl mx-auto">
          {/* Rich Card Header */}
          <motion.div
            initial={{ opacity: 0, y: -20 }}
            animate={{ opacity: 1, y: 0 }}
            className="mb-8"
          >
            <div className="bg-white rounded-2xl shadow-xl border border-gray-100 overflow-hidden">
              {/* Card Header with Gradient */}
              <div className="bg-gradient-to-r from-orange-500 to-red-500 px-6 py-8 text-white">
                <div className="flex items-center gap-4 mb-4">
                  <div className="w-16 h-16 bg-white/20 rounded-2xl flex items-center justify-center backdrop-blur-sm">
                    <ChefHat className="w-8 h-8" />
                  </div>
                  <div>
                    <h1 className="text-2xl md:text-3xl font-bold mb-1">Fun with AI</h1>
                    <p className="text-orange-100 text-sm md:text-base">分享的餐厅推荐</p>
                  </div>
                </div>
              </div>

              {/* Card Content */}
              <div className="p-6 md:p-8">

                {/* Conversation */}
                <div className="space-y-6 mb-6">
                  {/* User Message */}
                  {content.query_text && (
                    <motion.div
                      initial={{ opacity: 0, x: -20 }}
                      animate={{ opacity: 1, x: 0 }}
                      className="flex gap-3 justify-end"
                    >
                      <div className="max-w-[85%] bg-gradient-to-r from-orange-500 to-red-500 text-white rounded-2xl px-5 py-4 shadow-lg">
                        <div className="flex items-center gap-2 mb-2">
                          <div className="w-6 h-6 bg-white/20 rounded-full flex items-center justify-center">
                            <User className="w-4 h-4" />
                          </div>
                          <span className="text-sm font-semibold">用户</span>
                        </div>
                        <p className="whitespace-pre-wrap break-words leading-relaxed">{content.query_text}</p>
                      </div>
                    </motion.div>
                  )}

                  {/* AI Message */}
                  {content.message_content && (
                    <motion.div
                      initial={{ opacity: 0, x: 20 }}
                      animate={{ opacity: 1, x: 0 }}
                      className="flex gap-3"
                    >
                      <div className="flex-shrink-0 w-10 h-10 bg-gradient-to-br from-orange-500 to-red-500 rounded-full flex items-center justify-center shadow-lg">
                        <Bot className="w-6 h-6 text-white" />
                      </div>
                      <div className="max-w-[85%] bg-gray-50 border border-gray-200 text-gray-900 rounded-2xl px-5 py-4 shadow-sm">
                        <div className="flex items-center gap-2 mb-2">
                          <Bot className="w-4 h-4 text-orange-500" />
                          <span className="text-sm font-semibold text-gray-700">AI 助手</span>
                        </div>
                        <div className="whitespace-pre-wrap break-words leading-relaxed text-gray-800">
                          {content.message_content}
                        </div>
                      </div>
                    </motion.div>
                  )}
                </div>

                {/* Restaurant Recommendations */}
                {content.restaurants && content.restaurants.length > 0 && (
                  <motion.div
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    className="mb-6"
                  >
                    <div className="flex items-center gap-2 mb-4">
                      <div className="w-1 h-6 bg-gradient-to-b from-orange-500 to-red-500 rounded-full"></div>
                      <h2 className="text-xl md:text-2xl font-bold text-gray-900">推荐餐厅</h2>
                    </div>
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                      {content.restaurants.map((restaurant, index) => (
                        <RestaurantCard
                          key={restaurant.id || index}
                          restaurant={restaurant}
                          index={index}
                          variant="chat"
                        />
                      ))}
                    </div>
                  </motion.div>
                )}

                {/* Card Footer */}
                <div className="pt-6 border-t border-gray-200">
                  <div className="flex flex-col sm:flex-row items-center justify-between gap-4">
                    <div className="text-sm text-gray-500 space-y-1">
                      <p className="flex items-center gap-2">
                        <Share2 className="w-4 h-4" />
                        <span>由 Fun with AI 生成</span>
                      </p>
                      {content.created_at && (
                        <p className="text-xs">
                          分享时间: {new Date(content.created_at).toLocaleString('zh-CN')}
                        </p>
                      )}
                      {content.view_count !== undefined && (
                        <p className="text-xs">浏览次数: {content.view_count}</p>
                      )}
                    </div>
                    <button
                      onClick={() => navigate('/')}
                      className="px-6 py-3 bg-gradient-to-r from-orange-500 to-red-500 text-white rounded-lg hover:from-orange-600 hover:to-red-600 transition-all shadow-lg hover:shadow-xl font-medium"
                    >
                      开始使用 Fun with AI
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </motion.div>
        </div>
      </div>
    </>
  );
}

